import { LatestSpeakerStack } from "../../LatestSpeakerStack";
import { metricsManager as globalsMetricsManager } from "../../Globals";
import { Logger } from "../../Logger";
import { calculateBitrate, getBitRateByLayer, nextLayerDown, compareLayers, nextLayerUp, } from "../../utils";
import { difference, clone } from "ramda";
export const DEFAULT_SFU_INFO = {
    previousBytesReceived: 0,
    previousBytesReceivedTimestamp: 0,
    bitrateHistory: [],
    bandwidthFulfillment: {
        consumers: 0,
        expectedPixelRate: 0,
        receivedPixelRate: 0,
        players: {},
        totalConsumers: 0,
        screenshareBitrates: [],
    },
    bandwidthFulfillmentTimestamp: 0,
    lastFulfillmentTimestamp: 0,
    underThresholdWindow: [],
    lastActionTimestamp: 0,
    multipleUnderThresholdWindow: [],
    videoCounts: { paused: 0, total: 0 },
};
const ALL_TAGS = ["video", "audio", "screen", "sound"];
export var Action;
(function (Action) {
    Action["FullUp"] = "FullUp";
    Action["StepDownOrPause"] = "StepDownOrPause";
    Action["StepUp"] = "StepUp";
    Action["Unpause"] = "Unpause";
    Action["UnpauseSwap"] = "UnpauseSwap";
    Action["NoAction"] = "NoAction";
})(Action || (Action = {}));
const PIXEL_RATE_RATIO_THRESHOLD = 0.52;
const MIN_DELTA_FOR_UNPAUSE_PERCENTAGE = 1.5;
const MIN_DELTA_FOR_STEP_PERCENTAGE = 1.25;
const MIN_BITRATE_THRESHOLD = 60000;
const SCREENSHARE_PADDING_USAGE_RATIO = 0.75;
const UNDER_THRESHOLD_WINDOW_LEN = 4;
const UNDER_THRESHOLD_TRIGGER = UNDER_THRESHOLD_WINDOW_LEN - 1;
const OVER_THRESHOLD_TRIGGER = UNDER_THRESHOLD_WINDOW_LEN - 1;
const LOW_BITRATE_CUTOFF_WINDOW_LEN = 6;
const LOW_BITRATE_CUTOFF_TRIGGER = LOW_BITRATE_CUTOFF_WINDOW_LEN - 1;
const ALLOCATION_INTERVAL_MS = 5000;
const MIN_LAST_PAUSE_UNTIL_SWAP_MS = 14000;
const PAUSED_HISTORY_LIMIT_MS = 10000;
export const SFU_PRIORITY_CUTOFF = 16;
const MULTIPLE_UNDER_COUNT_THRESHOLD = 2;
const MULTIPLE_UNDER_THRESHOLD_WINDOW_LEN = 3;
const MULTIPLE_UNDER_THRESHOLD_TRIGGER = MULTIPLE_UNDER_THRESHOLD_WINDOW_LEN - 1;
const SFU_PAUSE_CHANGE_THRESHOLD = 2;
const MIN_LAST_SUBSCRIBE_CHANGE_UNTIL_CHECK_MS = 250;
const MAX_BITRATE_HISTORY_LEN = 3;
const MAX_LATEST_SPEAKERS = 0;
const MAX_HIGHEST_PRIORITY_SPEAKERS = 2;
export class BitrateManager {
    id;
    sfus;
    allocationInterval;
    sfuClientManager;
    rtcStatsManager;
    enabled = false;
    infoMap;
    underMinBitrateWindow;
    conversation;
    lastPauseTimestamp;
    pauseDueTo1on1;
    lastSubscribeChangeTimestamp;
    periodicLogs = false;
    consumerLayers = {};
    setStreamStatePaused;
    setMaxConsumerLayers = async (_id, _tag, _spatialLayer, _temporalLayer) => { };
    latestSpeakerStack;
    onInfo = (infoMap) => {
        let count = 0;
        Object.keys(infoMap).forEach((id) => {
            const consumerInfo = infoMap[id];
            if (consumerInfo) {
                const enabled = consumerInfo.enabled.video && !consumerInfo.muted.video;
                this.latestSpeakerStack.adjustSpeakerList(id, enabled);
                if (!enabled) {
                    delete this.consumerLayers[id];
                }
            }
            ++count;
        });
        this.pauseDueTo1on1 = count === 1;
        if (count === 0) {
            if (this.conversation.started) {
                this.sfus.clear();
                this.underMinBitrateWindow = [];
                this.conversation = { started: false };
                this.latestSpeakerStack.clear();
            }
        }
        else {
            this.conversation = { ...this.conversation, started: true };
            difference(Object.keys(this.infoMap), Object.keys(infoMap)).forEach((id) => {
                this.latestSpeakerStack.removeSpeaker(id);
            });
        }
        this.infoMap = { ...infoMap };
    };
    updateVideoSubscribed(sfu, videoTag) {
        const timeDiff = this.lastSubscribeChangeTimestamp
            ? Date.now() - this.lastSubscribeChangeTimestamp
            : null;
        this.lastSubscribeChangeTimestamp = Date.now();
        this.logIfEnabled(false, "videoSubscribed timeDiff", timeDiff, "sfu", sfu, videoTag);
    }
    updateMaxLayersWithCheck(id, spatialLayer, temporalLayer) {
        const layers = this.consumerLayers[id];
        const isLimited = layers &&
            (layers.desired.spatial !== layers.requested.spatial ||
                layers.desired.temporal !== layers.requested.temporal);
        const isRequestedLessThanDesired = layers &&
            compareLayers(spatialLayer ?? 0, temporalLayer ?? 1, layers.requested.spatial, layers.requested.temporal) >= 0;
        if (isLimited && isRequestedLessThanDesired) {
            this.updateDesiredLayers(id, spatialLayer, temporalLayer);
            return false;
        }
        this.updateDesiredLayers(id, spatialLayer, temporalLayer);
        this.updateRequestedLayers(id, spatialLayer, temporalLayer);
        return true;
    }
    updateDesiredLayers(id, spatial, temporal = 1) {
        const layers = this.consumerLayers[id];
        if (layers) {
            layers.desired.spatial = spatial ?? 0;
            layers.desired.temporal = temporal;
        }
        else {
            this.consumerLayers[id] = {
                desired: { spatial: spatial ?? 0, temporal },
                requested: { spatial: spatial ?? 0, temporal },
            };
        }
    }
    updateRequestedLayers(id, spatial, temporal = 1) {
        const layers = this.consumerLayers[id];
        if (layers) {
            layers.requested.spatial = spatial ?? 0;
            layers.requested.temporal = temporal;
        }
        else {
            this.logIfEnabled(false, "failed to find layers for", id);
            this.updateDesiredLayers(id, spatial, temporal);
        }
    }
    setHighestPriorityStreams(ids) {
        if (ids) {
            this.latestSpeakerStack.addHighestPrioritySpeakers(ids.length <= MAX_HIGHEST_PRIORITY_SPEAKERS
                ? ids
                : ids.slice(0, MAX_HIGHEST_PRIORITY_SPEAKERS));
        }
        else {
            this.latestSpeakerStack.clearHighestPriorityStreams();
        }
    }
    constructor(setStreamStatePaused) {
        this.id = "unknown";
        this.setStreamStatePaused = setStreamStatePaused;
        this.sfus = new Map();
        this.infoMap = {};
        this.latestSpeakerStack = new LatestSpeakerStack(MAX_LATEST_SPEAKERS, PAUSED_HISTORY_LIMIT_MS, (id) => this.pausedStreamRemoved(id));
        this.underMinBitrateWindow = [];
        this.conversation = { started: false };
        this.lastPauseTimestamp = 0;
        this.pauseDueTo1on1 = false;
        this.lastSubscribeChangeTimestamp = 0;
    }
    logIfEnabled(periodic, ...data) {
        if (this.enabled && (!periodic || this.periodicLogs)) {
            Logger.info("[BitM]", ...data);
        }
    }
    start(id, sfuClientManager, rtcStatsManager, enabled) {
        this.enabled = enabled;
        this.id = id;
        this.sfuClientManager = sfuClientManager;
        this.rtcStatsManager = rtcStatsManager;
        this.allocationInterval = setInterval(() => {
            this.updateAllocation();
        }, ALLOCATION_INTERVAL_MS);
        this.rtcStatsManager.onUpdateMetrics = (sfu, availableBitrate) => {
            const recvStats = this.rtcStatsManager?.getCachedRecvCandidatePairStats(sfu);
            const bytesReceived = recvStats?.bytesReceived ?? 0;
            const timestamp = recvStats?.timestamp ?? 0;
            const bitrates = { bitrate: 0, availableBitrate: availableBitrate };
            const sfuInfo = this.sfus.get(sfu);
            if (sfuInfo) {
                const timestampDelta = (timestamp - sfuInfo.previousBytesReceivedTimestamp) / 1000;
                bitrates.bitrate = calculateBitrate(bytesReceived, sfuInfo.previousBytesReceived, timestampDelta);
                sfuInfo.previousBytesReceived = bytesReceived;
                sfuInfo.previousBytesReceivedTimestamp = timestamp;
                if (sfuInfo.bitrateHistory.unshift(bitrates) > MAX_BITRATE_HISTORY_LEN) {
                    sfuInfo.bitrateHistory.length = MAX_BITRATE_HISTORY_LEN;
                }
            }
            else {
                this.sfus.set(sfu, {
                    ...clone(DEFAULT_SFU_INFO),
                    previousBytesReceived: bytesReceived,
                    previousBytesReceivedTimestamp: timestamp,
                    bitrateHistory: [bitrates],
                });
            }
            this.logIfEnabled(true, `sfuInfo ${bitrates.availableBitrate} - ${bitrates.bitrate.toFixed(1)} = ${((bitrates.availableBitrate - bitrates.bitrate) /
                1000).toFixed(1)}K`);
        };
    }
    destroy() {
        if (this.allocationInterval) {
            clearInterval(this.allocationInterval);
            this.allocationInterval = undefined;
        }
        this.sfus = new Map();
        this.infoMap = {};
        this.latestSpeakerStack.destroy();
        this.consumerLayers = {};
        this.underMinBitrateWindow = [];
        this.conversation = { started: false };
        this.pauseDueTo1on1 = false;
    }
    pausedStreamRemoved(id) {
        this.logIfEnabled(false, "removed paused", id);
        this.setStreamStatePaused(id, false);
    }
    unpauseAll() {
        this.logIfEnabled(false, "unpause all");
        const latestSpeakers = this.latestSpeakerStack.getLatestSpeakers();
        latestSpeakers.forEach((speaker) => {
            if (speaker.paused) {
                this.latestSpeakerStack.setLatestSpeakerPaused(speaker.id, false);
                this.setStreamStatePaused(speaker.id, false);
            }
        });
    }
    consumerEnabled(srcId) {
        this.latestSpeakerStack.setLatestSpeakerPaused(srcId, false);
    }
    updateWindow(window, length, under) {
        if (window.length >= length) {
            window.shift();
        }
        window.push(under);
        return window.filter((v) => v).length;
    }
    sendChangeMetric(sfu, sfuInfo, reason) {
        const bitrates = sfuInfo.bitrateHistory[0] ?? { availableBitrate: 0, bitrate: 0 };
        const bandwidthFulfillment = sfuInfo.bandwidthFulfillment;
        const now = Date.now();
        globalsMetricsManager.addMetric("av-bitrate-m", {
            reason,
            sfu,
            timeSinceLastAction: Math.max(0, sfuInfo.lastActionTimestamp - now),
            availableBitrate: bitrates.availableBitrate,
            bitrate: bitrates.bitrate,
            prevAvailableBitrate: sfuInfo.bitrateHistory[1]?.availableBitrate ?? 0,
            prevBitrate: sfuInfo.bitrateHistory[1]?.bitrate ?? 0,
            prevPrevAvailableBitrate: sfuInfo.bitrateHistory[2]?.availableBitrate ?? 0,
            prevPrevBitrate: sfuInfo.bitrateHistory[2]?.bitrate ?? 0,
            pixelRateRatio: bandwidthFulfillment.receivedPixelRate / bandwidthFulfillment.expectedPixelRate,
        });
        sfuInfo.lastActionTimestamp = now;
    }
    pauseSpeaker(id) {
        this.logIfEnabled(false, "action pause", id);
        if (id) {
            this.latestSpeakerStack.setLatestSpeakerPaused(id, true);
            this.setStreamStatePaused(id, true);
            this.lastPauseTimestamp = Date.now();
        }
    }
    calcBitrateNeededForNextLayerUp(id) {
        const layers = this.consumerLayers[id];
        if (!layers)
            return 0;
        const { spatial, temporal } = layers.requested;
        const nextLayers = nextLayerUp(spatial, temporal);
        return (getBitRateByLayer(nextLayers.spatialLayer, nextLayers.temporalLayer) -
            getBitRateByLayer(spatial, temporal));
    }
    getDesiredBitrateAndAction(speakers) {
        const hasHighestPrioritySpeakers = this.latestSpeakerStack.getLatestSpeakersWithHighestPriority().length > 0;
        const highestPrioritySpeakerCanImprove = speakers.filter((speaker) => speaker.highestPriority && !this.speakerAtDesiredLayers(speaker))
            .length > 0;
        const highestPrioritySpeakersPaused = speakers.filter((speaker) => speaker.highestPriority && speaker.paused).length > 0;
        if (!highestPrioritySpeakerCanImprove || highestPrioritySpeakersPaused) {
            const pausedSpeakers = speakers.filter((speaker) => speaker.paused);
            const firstPausedSpeaker = pausedSpeakers[0];
            if (firstPausedSpeaker) {
                const spatialLayer = this.consumerLayers[firstPausedSpeaker.id]?.desired.spatial ?? 0;
                const bitrateDesired = hasHighestPrioritySpeakers && spatialLayer === 0
                    ? getBitRateByLayer(0, 1)
                    : getBitRateByLayer(Math.min(spatialLayer, 1), 0);
                return { bitrateDesired, action: Action.Unpause };
            }
        }
        const bitratesDesired = speakers.map((speaker) => this.speakerAtDesiredLayers(speaker) ? 0 : this.calcBitrateNeededForNextLayerUp(speaker.id));
        return { bitrateDesired: Math.max(...bitratesDesired), action: Action.StepUp };
    }
    takeSpeakerAction(speakerAction) {
        this.logIfEnabled(false, "action", speakerAction.action, speakerAction.id);
        this.latestSpeakerStack.setLatestSpeakerPaused(speakerAction.id, false);
        this.setStreamStatePaused(speakerAction.id, false);
        this.sendChangeMetric(speakerAction.sfu, speakerAction.sfuInfo, speakerAction.action === Action.UnpauseSwap ? "swap" : "bitrate-under");
        const layers = this.consumerLayers[speakerAction.id];
        if (layers) {
            if (speakerAction.action === Action.UnpauseSwap) {
                layers.requested.spatial = layers.desired.spatial;
                layers.requested.temporal = 0;
            }
            else {
                layers.requested.spatial = layers.desired.spatial;
                layers.requested.temporal = layers.desired.temporal;
            }
            this.setMaxConsumerLayers(speakerAction.id, "video", layers.requested.spatial, layers.requested.temporal);
        }
    }
    takeSfuAction(sfuAction) {
        this.logIfEnabled(false, "action", sfuAction.action, sfuAction.sfu);
        if (sfuAction.action === Action.Unpause) {
            const hasHighestPrioritySpeakers = this.latestSpeakerStack.getLatestSpeakersWithHighestPriority().length > 0;
            const pausedSpeakers = sfuAction.speakers.filter((speaker) => speaker.paused);
            const firstSpeaker = pausedSpeakers[0];
            if (firstSpeaker) {
                const spatialLayer = this.consumerLayers[firstSpeaker.id]?.desired.spatial ?? 0;
                const requestedSpatialLayer = Math.min(spatialLayer, 1);
                const requestedTemporalLayer = hasHighestPrioritySpeakers && requestedSpatialLayer === 0 ? 1 : 0;
                this.updateRequestedLayers(firstSpeaker.id, requestedSpatialLayer, requestedTemporalLayer);
                this.latestSpeakerStack.setLatestSpeakerPaused(firstSpeaker.id, false);
                this.setStreamStatePaused(firstSpeaker.id, false);
                this.sendChangeMetric(sfuAction.sfu, sfuAction.sfuInfo, "bitrate-unpause");
                this.setMaxConsumerLayers(firstSpeaker.id, "video", requestedSpatialLayer, requestedTemporalLayer);
            }
            return;
        }
        const layerChanges = [];
        sfuAction.speakers.forEach((speaker) => {
            if (!speaker.paused && !this.speakerAtDesiredLayers(speaker)) {
                const layers = this.consumerLayers[speaker.id];
                if (layers) {
                    const { spatialLayer: nextSpatial, temporalLayer: nextTemporal } = nextLayerUp(layers.requested.spatial, layers.requested.temporal);
                    this.updateRequestedLayers(speaker.id, nextSpatial, nextTemporal);
                    layerChanges.push({
                        id: speaker.id,
                        requested: { spatial: nextSpatial, temporal: nextTemporal },
                    });
                }
            }
        });
        this.sendChangeMetric(sfuAction.sfu, sfuAction.sfuInfo, "bitrate-under");
        layerChanges.forEach((changes) => {
            this.setMaxConsumerLayers(changes.id, "video", changes.requested.spatial, changes.requested.temporal);
        });
    }
    checkForMinimumBitrateCutoff() {
        if (!this.conversation.started)
            return;
        let minBitrateSfu = undefined;
        for (const [sfu, sfuInfo] of this.sfus) {
            if (sfuInfo.bitrateHistory.length < MAX_BITRATE_HISTORY_LEN) {
                continue;
            }
            const availableBitrate = sfuInfo.bitrateHistory[0]?.availableBitrate ?? 0;
            const bitrate = sfuInfo.bitrateHistory[0]?.bitrate ?? 0;
            if (availableBitrate > 0 &&
                (!minBitrateSfu ||
                    (availableBitrate < minBitrateSfu.availableBitrate && bitrate < minBitrateSfu.bitrate))) {
                minBitrateSfu = { sfu, sfuInfo, availableBitrate, bitrate };
            }
        }
        if (minBitrateSfu) {
            const underMinCount = this.updateWindow(this.underMinBitrateWindow, LOW_BITRATE_CUTOFF_WINDOW_LEN, minBitrateSfu.availableBitrate < MIN_BITRATE_THRESHOLD &&
                minBitrateSfu.bitrate < MIN_BITRATE_THRESHOLD);
            if (underMinCount >= LOW_BITRATE_CUTOFF_TRIGGER) {
                if (!this.conversation.hadCutoff) {
                    this.conversation = { ...this.conversation, hadCutoff: true };
                    this.sendChangeMetric(minBitrateSfu.sfu, minBitrateSfu.sfuInfo, "bitrate-cutoff");
                    this.logIfEnabled(false, "cutoff", minBitrateSfu.sfu);
                }
            }
        }
    }
    getSfuAndInfoById(id) {
        const sfu = this.sfuClientManager?.getLatestSFUAddrByPlayerId(id);
        if (sfu) {
            const sfuInfo = this.sfus.get(sfu);
            if (sfuInfo)
                return { sfu, sfuInfo };
            this.logIfEnabled(false, "failed to find sfuInfo for", sfu);
            return undefined;
        }
        this.logIfEnabled(false, "failed to get sfu for", id);
        return undefined;
    }
    checkForSpeakerSwap() {
        const now = Date.now();
        if (now - this.lastPauseTimestamp < MIN_LAST_PAUSE_UNTIL_SWAP_MS)
            return;
        const swap = this.latestSpeakerStack.getSwappableSpeakers();
        if (!swap || !swap.unpausedId || !swap.pausedId)
            return;
        const unpausedInfo = this.getSfuAndInfoById(swap.unpausedId);
        if (!unpausedInfo) {
            this.logIfEnabled(false, "Failed swapping for unpaused", swap.unpausedId);
            return;
        }
        const pausedInfo = this.getSfuAndInfoById(swap.pausedId);
        if (!pausedInfo) {
            this.logIfEnabled(false, "Failed swapping for paused", swap.pausedId);
            return;
        }
        this.logIfEnabled(false, "swapping", swap.pausedId, "->", swap.unpausedId);
        this.pauseSpeaker(swap.unpausedId);
        this.sendChangeMetric(unpausedInfo.sfu, unpausedInfo.sfuInfo, "swap");
        const speakerToImprove = {
            id: swap.pausedId,
            action: Action.UnpauseSwap,
            sfu: pausedInfo.sfu,
            sfuInfo: pausedInfo.sfuInfo,
        };
        this.takeSpeakerAction(speakerToImprove);
    }
    getOptimizePriorityList(srcIds, previousPriorities) {
        const additionalSrcIds = [];
        let foundDiff = false;
        for (const [i, id] of srcIds.entries()) {
            const prevIndex = previousPriorities.indexOf(id);
            if (i === prevIndex) {
                continue;
            }
            if (i >= SFU_PRIORITY_CUTOFF) {
                if (prevIndex >= SFU_PRIORITY_CUTOFF || prevIndex < 0) {
                    continue;
                }
                else {
                    foundDiff = true;
                    additionalSrcIds.push(id);
                }
            }
            else {
                foundDiff = true;
            }
        }
        if (!foundDiff)
            return undefined;
        const truncatedSrcIds = srcIds.slice(0, SFU_PRIORITY_CUTOFF);
        return truncatedSrcIds.concat(additionalSrcIds);
    }
    async adjustPrioritiesIfChanged() {
        if (!this.latestSpeakerStack.hasPriorityChanged())
            return;
        const latestSpeakers = this.latestSpeakerStack.getLatestSpeakers();
        const sfuLatestSpeakers = new Map();
        latestSpeakers.forEach((speaker) => {
            if (!speaker.paused) {
                const sfu = this.sfuClientManager?.getLatestSFUAddrByPlayerId(speaker.id);
                if (sfu) {
                    const speakers = sfuLatestSpeakers.get(sfu);
                    speakers ? speakers.push(speaker) : sfuLatestSpeakers.set(sfu, [speaker]);
                }
            }
        });
        for (const [sfu, speakers] of sfuLatestSpeakers) {
            const sfuInfo = this.sfus.get(sfu);
            const srcIds = speakers.map((speaker) => speaker.id);
            let optimizedSrcIds = srcIds.slice(0, SFU_PRIORITY_CUTOFF);
            if (sfuInfo) {
                if (sfuInfo.previousPriorities) {
                    optimizedSrcIds = this.getOptimizePriorityList(srcIds, sfuInfo.previousPriorities);
                }
                sfuInfo.previousPriorities = srcIds;
                if (!optimizedSrcIds) {
                    continue;
                }
            }
            for (const tag of ALL_TAGS) {
                await this.sfuClientManager?.sfuClients[sfu]?.setConsumePriorityByTag(tag, optimizedSrcIds);
            }
        }
    }
    reduceSpeaker(sfu, sfuInfo, speaker, highestPrioritySpeakersOverride = false) {
        if (!speaker)
            return;
        const requested = this.consumerLayers[speaker.id]?.requested;
        let reason = "bitrate-pause";
        if (!requested) {
            this.pauseSpeaker(speaker.id);
        }
        else {
            const { spatialLayer: nextSpatialLayer, temporalLayer: nextTemporalLayer } = nextLayerDown(requested.spatial, requested.temporal);
            if (nextSpatialLayer === undefined || nextTemporalLayer === undefined) {
                this.pauseSpeaker(speaker.id);
            }
            else if (highestPrioritySpeakersOverride &&
                nextSpatialLayer === 0 &&
                nextTemporalLayer === 0) {
                this.pauseSpeaker(speaker.id);
            }
            else if (nextSpatialLayer === 0 && nextTemporalLayer === 1) {
                this.pauseSpeaker(speaker.id);
            }
            else {
                reason = "bitrate-over";
                this.logIfEnabled(false, "action reduce", speaker.id, requested.spatial, "@", requested.temporal, "->", nextSpatialLayer, "@", nextTemporalLayer);
                this.updateRequestedLayers(speaker.id, nextSpatialLayer, nextTemporalLayer);
                this.setMaxConsumerLayers(speaker.id, "video", nextSpatialLayer, nextTemporalLayer);
            }
        }
        this.sendChangeMetric(sfu, sfuInfo, reason);
    }
    findAndReduceSpeaker(sfu, sfuInfo, speakers) {
        const highestPrioritySpeakers = this.latestSpeakerStack.getLatestSpeakersWithHighestPriority();
        if (highestPrioritySpeakers.length > 0) {
            this.reduceSpeaker(sfu, sfuInfo, speakers[speakers.length - 1], true);
            return;
        }
        for (let spatialLayer = 2; spatialLayer >= 0; --spatialLayer) {
            for (let temporalLayer = 1; temporalLayer >= 0; --temporalLayer) {
                const speakersAtLayers = speakers.filter((speaker) => {
                    const requested = this.consumerLayers[speaker.id]?.requested ?? {
                        spatial: 0,
                        temporal: 0,
                    };
                    return requested.spatial === spatialLayer && requested.temporal === temporalLayer;
                });
                if (speakersAtLayers.length > 0) {
                    this.reduceSpeaker(sfu, sfuInfo, speakersAtLayers[speakersAtLayers.length - 1]);
                    return;
                }
            }
        }
    }
    speakerAtDesiredLayers(speaker) {
        if (speaker) {
            const layers = this.consumerLayers[speaker.id];
            if (layers &&
                (layers.desired.spatial !== layers.requested.spatial ||
                    layers.desired.temporal !== layers.requested.temporal)) {
                return false;
            }
        }
        return true;
    }
    updateStatWindows(sfuLatestSpeakers) {
        for (const [sfu, sfuInfo] of this.sfus) {
            const speakers = sfuLatestSpeakers.get(sfu);
            if (!speakers || speakers.length <= 0 || sfuInfo.bandwidthFulfillment.consumers <= 0) {
                this.updateWindow(sfuInfo.underThresholdWindow, UNDER_THRESHOLD_WINDOW_LEN, false);
                this.updateWindow(sfuInfo.multipleUnderThresholdWindow, MULTIPLE_UNDER_THRESHOLD_WINDOW_LEN, false);
                continue;
            }
            if (sfuInfo.bandwidthFulfillmentTimestamp === sfuInfo.lastFulfillmentTimestamp) {
                continue;
            }
            sfuInfo.lastFulfillmentTimestamp = sfuInfo.bandwidthFulfillmentTimestamp;
            const bandwidthFulfillment = sfuInfo.bandwidthFulfillment;
            const lowPixelRateRatios = Object.values(bandwidthFulfillment.players)
                .map((player) => player.receivedPixelRate / player.expectedPixelRate)
                .filter((pixelRateRatio) => pixelRateRatio >= 0 && pixelRateRatio < PIXEL_RATE_RATIO_THRESHOLD);
            const underConsumer = lowPixelRateRatios.length > 0;
            if (underConsumer) {
                this.logIfEnabled(false, "underConsume @", bandwidthFulfillment.consumers, lowPixelRateRatios, bandwidthFulfillment.players);
            }
            const maxBitrateDelta = Math.max(...Object.values(bandwidthFulfillment.players).map((player) => player.additionalBitrateNeeded));
            let underProducer = false;
            if (maxBitrateDelta !== Number.NEGATIVE_INFINITY) {
                const bitrates = sfuInfo.bitrateHistory[0];
                if (bitrates) {
                    const availableDelta = bitrates.availableBitrate - bitrates.bitrate;
                    if (availableDelta > 0 && availableDelta >= maxBitrateDelta) {
                        underProducer = true;
                    }
                    this.logIfEnabled(false, `deltas underProduce=${underProducer}: ${(availableDelta / 1000).toFixed(1)}K vs ${(maxBitrateDelta / 1000).toFixed(1)}K @ ${bandwidthFulfillment.consumers}`);
                }
            }
            this.updateWindow(sfuInfo.underThresholdWindow, UNDER_THRESHOLD_WINDOW_LEN, !underProducer && underConsumer);
            this.updateWindow(sfuInfo.multipleUnderThresholdWindow, MULTIPLE_UNDER_THRESHOLD_WINDOW_LEN, !underProducer && lowPixelRateRatios.length >= MULTIPLE_UNDER_COUNT_THRESHOLD);
        }
    }
    isUpgradePossible(sfuInfo, speakers) {
        const minBitrateDelta = Math.min(...sfuInfo.bitrateHistory.map((bitrate) => bitrate.availableBitrate - bitrate.bitrate));
        const { bitrateDesired, action } = this.getDesiredBitrateAndAction(speakers);
        const screensharePadding = this.calcScreensharePadding(sfuInfo.bandwidthFulfillment);
        const factor = action === Action.StepUp ? MIN_DELTA_FOR_STEP_PERCENTAGE : MIN_DELTA_FOR_UNPAUSE_PERCENTAGE;
        const bitrateNeeded = bitrateDesired * factor;
        if (bitrateDesired > 0 && minBitrateDelta - screensharePadding > bitrateNeeded) {
            this.logIfEnabled(false, "minBitrateDelta", minBitrateDelta, "screensharePadding", screensharePadding, "bitrateDesired", bitrateDesired, "ratio", (minBitrateDelta - screensharePadding) / bitrateDesired, action);
            return { canUpgrade: true, action };
        }
        return { canUpgrade: false, action: Action.NoAction };
    }
    buildSfuActionMap(sfuLatestSpeakers, sfuPausedOrLimitedSpeakers) {
        const sfuActions = new Map();
        for (const [sfu, sfuInfo] of this.sfus) {
            const speakers = sfuLatestSpeakers.get(sfu);
            if (speakers && speakers.length > 0) {
                const underCount = sfuInfo.underThresholdWindow.filter((v) => v).length;
                const multipleUnderCount = sfuInfo.multipleUnderThresholdWindow.filter((v) => v).length;
                const multipleUnderOverride = multipleUnderCount >= MULTIPLE_UNDER_THRESHOLD_TRIGGER;
                if (multipleUnderOverride) {
                    this.logIfEnabled(false, "multiple override for videos under");
                }
                if (multipleUnderOverride || underCount >= UNDER_THRESHOLD_TRIGGER) {
                    sfuActions.set(sfu, Action.StepDownOrPause);
                    continue;
                }
            }
            const pausedOrLimitedSpeakers = sfuPausedOrLimitedSpeakers.get(sfu);
            if (pausedOrLimitedSpeakers && pausedOrLimitedSpeakers.length > 0) {
                const overCount = sfuInfo.underThresholdWindow.filter((v) => !v).length;
                if (overCount < OVER_THRESHOLD_TRIGGER) {
                    continue;
                }
                const { canUpgrade, action } = this.isUpgradePossible(sfuInfo, pausedOrLimitedSpeakers);
                if (canUpgrade) {
                    sfuActions.set(sfu, action);
                }
            }
        }
        return sfuActions;
    }
    getSFUAddrByPlayerId(id) {
        return this.sfuClientManager?.getLatestSFUAddrByPlayerId(id) ?? "";
    }
    coordinateAmongSfus(sfuActions, sfuLatestSpeakers, sfuPausedOrLimitedSpeakers) {
        if (sfuActions.size === 0)
            return { targetSfu: "", downgraded: false, upgraded: false };
        const upCount = Array.from(sfuActions.values()).filter((action) => action === Action.StepUp || action === Action.Unpause).length;
        const downCount = Array.from(sfuActions.values()).filter((action) => action === Action.StepDownOrPause).length;
        const latestSpeakers = this.latestSpeakerStack.getLatestSpeakers();
        if (latestSpeakers.length <= 0)
            return { targetSfu: "", downgraded: false, upgraded: false };
        const mostRecentSpeaker = latestSpeakers.filter((s) => {
            const sfu = this.getSFUAddrByPlayerId(s.id);
            return sfu && sfuActions.has(sfu);
        })[0];
        const mostRecentSpeakerSfu = this.getSFUAddrByPlayerId(mostRecentSpeaker?.id ?? "");
        if (!mostRecentSpeakerSfu) {
            this.logIfEnabled(false, "Could not find most recent speaker's SFU", mostRecentSpeaker?.id);
            return { targetSfu: "", downgraded: false, upgraded: false };
        }
        this.logIfEnabled(true, "most recent speaker", mostRecentSpeaker?.id, mostRecentSpeakerSfu);
        const leastRecentSpeakerOnDowngradableSfu = latestSpeakers
            .filter((s) => {
            const sfu = this.getSFUAddrByPlayerId(s.id);
            return sfu && sfuActions.get(sfu) === Action.StepDownOrPause;
        })
            .slice(-1)[0];
        const leastRecentSpeakerSfu = this.getSFUAddrByPlayerId(leastRecentSpeakerOnDowngradableSfu?.id ?? "");
        this.logIfEnabled(true, "least recent speaker", leastRecentSpeakerOnDowngradableSfu?.id, leastRecentSpeakerSfu);
        if (upCount > 0 && downCount === 0) {
            const sfu = mostRecentSpeakerSfu;
            const sfuInfo = this.sfus.get(mostRecentSpeakerSfu);
            const action = sfuActions.get(mostRecentSpeakerSfu);
            const speakers = sfuPausedOrLimitedSpeakers.get(mostRecentSpeakerSfu);
            if (sfuInfo && action && speakers) {
                const sfuToImprove = {
                    sfu,
                    sfuInfo,
                    action,
                    speakers,
                };
                this.takeSfuAction(sfuToImprove);
                this.logIfEnabled(false, `All SFU can upgrade, upgrade ${sfu} for most recent speaker ${mostRecentSpeaker?.id}`);
                return { targetSfu: sfu, upgraded: true, downgraded: false };
            }
            else {
                this.logIfEnabled(false, "Could not upgrade", sfuInfo, action, speakers);
                return { targetSfu: "", downgraded: false, upgraded: false };
            }
        }
        else if (upCount === 0 && downCount > 0) {
            if (!leastRecentSpeakerSfu) {
                this.logIfEnabled(false, "Could not find least recent speaker's SFU", leastRecentSpeakerOnDowngradableSfu?.id);
                return { targetSfu: "", downgraded: false, upgraded: false };
            }
            const sfuInfo = this.sfus.get(leastRecentSpeakerSfu);
            const speakers = sfuLatestSpeakers.get(leastRecentSpeakerSfu);
            if (sfuInfo && speakers) {
                this.findAndReduceSpeaker(leastRecentSpeakerSfu, sfuInfo, speakers);
                this.logIfEnabled(false, `All SFU can downgrade, downgrade ${leastRecentSpeakerSfu} for least recent speaker ${leastRecentSpeakerOnDowngradableSfu?.id}`);
                return { targetSfu: leastRecentSpeakerSfu, downgraded: true, upgraded: false };
            }
            else {
                this.logIfEnabled(false, "Could not downgrade", leastRecentSpeakerSfu, sfuInfo, speakers);
                return { targetSfu: "", downgraded: false, upgraded: false };
            }
        }
        else {
            if (sfuActions.get(mostRecentSpeakerSfu) === Action.StepUp ||
                sfuActions.get(mostRecentSpeakerSfu) === Action.Unpause) {
                const sfu = mostRecentSpeakerSfu;
                const sfuInfo = this.sfus.get(mostRecentSpeakerSfu);
                const action = sfuActions.get(mostRecentSpeakerSfu);
                const speakers = sfuPausedOrLimitedSpeakers.get(mostRecentSpeakerSfu);
                if (sfuInfo && action && speakers) {
                    const sfuToImprove = {
                        sfu,
                        sfuInfo,
                        action,
                        speakers,
                    };
                    this.takeSfuAction(sfuToImprove);
                    this.logIfEnabled(false, `Mixed SFU upgrade/downgrade, upgrade ${mostRecentSpeakerSfu} for most recent speaker ${mostRecentSpeaker?.id}`);
                    return { targetSfu: sfu, upgraded: true, downgraded: false };
                }
                else {
                    this.logIfEnabled(false, "Could not upgrade", sfuInfo, action, speakers);
                    return { targetSfu: "", downgraded: false, upgraded: false };
                }
            }
            else if (downCount === 1) {
                const sfuInfo = this.sfus.get(mostRecentSpeakerSfu);
                const speakers = sfuLatestSpeakers.get(mostRecentSpeakerSfu);
                if (sfuInfo && speakers) {
                    this.findAndReduceSpeaker(mostRecentSpeakerSfu, sfuInfo, speakers);
                    this.logIfEnabled(false, `Mixed SFU upgrade/downgrade, downgrade ${mostRecentSpeakerSfu} for most recent speaker ${mostRecentSpeaker?.id}`);
                    return { targetSfu: mostRecentSpeakerSfu, downgraded: true, upgraded: false };
                }
                else {
                    this.logIfEnabled(false, "Could not downgrade", mostRecentSpeakerSfu, sfuInfo, speakers);
                    return { targetSfu: "", downgraded: false, upgraded: false };
                }
            }
            else {
                if (!leastRecentSpeakerSfu) {
                    this.logIfEnabled(false, "Could not find least recent speaker's SFU", leastRecentSpeakerOnDowngradableSfu?.id);
                    return { targetSfu: "", downgraded: false, upgraded: false };
                }
                const sfuInfo = this.sfus.get(leastRecentSpeakerSfu);
                const speakers = sfuLatestSpeakers.get(leastRecentSpeakerSfu);
                if (sfuInfo && speakers) {
                    this.findAndReduceSpeaker(leastRecentSpeakerSfu, sfuInfo, speakers);
                    this.logIfEnabled(false, `Mixed SFU upgrade/downgrade, downgrade ${leastRecentSpeakerSfu} for least recent speaker ${leastRecentSpeakerOnDowngradableSfu?.id}`);
                    return { targetSfu: leastRecentSpeakerSfu, downgraded: true, upgraded: false };
                }
                else {
                    this.logIfEnabled(false, "Could not downgrade", leastRecentSpeakerSfu, sfuInfo, speakers);
                    return { targetSfu: "", downgraded: false, upgraded: false };
                }
            }
        }
    }
    calcScreensharePadding(bandwidthFulfillment) {
        let screensharePadding = Number.MAX_VALUE;
        for (const bitrates of bandwidthFulfillment.screenshareBitrates) {
            const usageRatio = bitrates.receivedBitrate / bitrates.targetBitrate;
            if (usageRatio < SCREENSHARE_PADDING_USAGE_RATIO) {
                const possiblePadding = bitrates.targetBitrate * SCREENSHARE_PADDING_USAGE_RATIO - bitrates.receivedBitrate;
                if (possiblePadding > 0 && possiblePadding < screensharePadding) {
                    screensharePadding = possiblePadding;
                }
            }
            else {
                screensharePadding = 0;
            }
        }
        return screensharePadding === Number.MAX_VALUE ? 0 : screensharePadding;
    }
    handleGridPageChanges(sfuVideoCounts, sfuLatestSpeakers) {
        const largeUnpausedChangeSfus = [];
        for (const [sfu, sfuInfo] of this.sfus) {
            const videoCounts = sfuVideoCounts.get(sfu);
            if (videoCounts && sfuInfo.videoCounts.paused > 0) {
                const previousUnpaused = sfuInfo.videoCounts.total - sfuInfo.videoCounts.paused;
                const currentUnpaused = videoCounts.total - videoCounts.paused;
                const delta = currentUnpaused - previousUnpaused;
                this.logIfEnabled(true, "unpaused delta", delta, "sfu", sfu);
                if (delta >= SFU_PAUSE_CHANGE_THRESHOLD) {
                    largeUnpausedChangeSfus.push({ sfu, delta });
                }
            }
            sfuInfo.videoCounts = videoCounts ?? { paused: 0, total: 0 };
        }
        if (largeUnpausedChangeSfus.length > 0) {
            largeUnpausedChangeSfus.forEach((sfuChange) => {
                this.logIfEnabled(false, `grid change pause ${sfuChange.delta} players`);
                const unpausedSpeakers = sfuLatestSpeakers.get(sfuChange.sfu);
                if (unpausedSpeakers) {
                    const end = Math.max(0, unpausedSpeakers.length - sfuChange.delta);
                    for (let i = unpausedSpeakers.length - 1; i >= end; --i) {
                        this.pauseSpeaker(unpausedSpeakers[i]?.id);
                    }
                }
            });
            return true;
        }
        return false;
    }
    updateAllocation() {
        this.checkForMinimumBitrateCutoff();
        const latestSpeakers = this.latestSpeakerStack.getLatestSpeakers();
        if (latestSpeakers.length <= 0)
            return;
        const timeDiff = Date.now() - this.lastSubscribeChangeTimestamp;
        const countVideos = timeDiff >= MIN_LAST_SUBSCRIBE_CHANGE_UNTIL_CHECK_MS;
        const sfuLatestSpeakers = new Map();
        const sfuPausedOrLimitedSpeakers = new Map();
        const sfuVideoCounts = new Map();
        latestSpeakers.forEach((speaker) => {
            const sfu = this.sfuClientManager?.getLatestSFUAddrByPlayerId(speaker.id);
            if (!sfu)
                return;
            const speakerMap = speaker.paused ? sfuPausedOrLimitedSpeakers : sfuLatestSpeakers;
            const speakers = speakerMap.get(sfu);
            speakers ? speakers.push(speaker) : speakerMap.set(sfu, [speaker]);
            if (!speaker.paused && !this.speakerAtDesiredLayers(speaker)) {
                const pausedSpeakers = sfuPausedOrLimitedSpeakers.get(sfu);
                pausedSpeakers
                    ? pausedSpeakers.push(speaker)
                    : sfuPausedOrLimitedSpeakers.set(sfu, [speaker]);
            }
            if (countVideos) {
                const videoCounts = sfuVideoCounts.get(sfu) ?? { paused: 0, total: 0 };
                if (speaker.paused) {
                    ++videoCounts.paused;
                }
                ++videoCounts.total;
                sfuVideoCounts.set(sfu, videoCounts);
            }
        });
        this.updateStatWindows(sfuLatestSpeakers);
        if (!this.enabled)
            return;
        if (this.pauseDueTo1on1) {
            const id = latestSpeakers[0]?.id ?? "";
            const sfu = this.sfuClientManager?.getLatestSFUAddrByPlayerId(id) ?? "";
            const sfuInfo = this.sfus.get(sfu);
            if (id && sfu && sfuInfo && sfuPausedOrLimitedSpeakers.size > 0) {
                const speakerToUnpause = {
                    id,
                    sfu,
                    sfuInfo,
                    action: Action.FullUp,
                };
                this.takeSpeakerAction(speakerToUnpause);
            }
            return;
        }
        if (countVideos) {
            if (this.handleGridPageChanges(sfuVideoCounts, sfuLatestSpeakers)) {
                this.adjustPrioritiesIfChanged();
                return;
            }
        }
        const sfuActions = this.buildSfuActionMap(sfuLatestSpeakers, sfuPausedOrLimitedSpeakers);
        if (sfuActions.size > 0) {
            this.logIfEnabled(false, "sfuActions", JSON.stringify(Array.from(sfuActions)));
        }
        const { upgraded, downgraded } = this.coordinateAmongSfus(sfuActions, sfuLatestSpeakers, sfuPausedOrLimitedSpeakers);
        if (upgraded || downgraded) {
            for (const [_sfu, sfuInfo] of this.sfus) {
                sfuInfo.underThresholdWindow = [];
                sfuInfo.multipleUnderThresholdWindow = [];
            }
        }
        else {
            this.checkForSpeakerSwap();
        }
        this.adjustPrioritiesIfChanged();
    }
    setBandwidthFulfillment(sfu, bandwidthFulfillment) {
        const sfuInfo = this.sfus.get(sfu);
        if (sfuInfo) {
            sfuInfo.bandwidthFulfillment = bandwidthFulfillment;
            sfuInfo.bandwidthFulfillmentTimestamp = Date.now();
        }
        else {
            this.sfus.set(sfu, {
                ...clone(DEFAULT_SFU_INFO),
                bandwidthFulfillment,
                bandwidthFulfillmentTimestamp: Date.now(),
            });
        }
    }
    setTestSFUBitrate(sfu, bitrate, availableBitrate) {
        const sfuInfo = this.sfus.get(sfu) ?? { ...clone(DEFAULT_SFU_INFO) };
        sfuInfo.bitrateHistory.unshift({ bitrate, availableBitrate });
        this.sfus.set(sfu, sfuInfo);
    }
    setTestSFUStatWindow(sfu, under, multipleUnder) {
        const sfuInfo = this.sfus.get(sfu) ?? { ...clone(DEFAULT_SFU_INFO) };
        sfuInfo.underThresholdWindow.push(under);
        sfuInfo.multipleUnderThresholdWindow.push(multipleUnder);
        this.sfus.set(sfu, sfuInfo);
    }
    getSFUInfo(sfu) {
        return this.sfus.get(sfu);
    }
}
//# sourceMappingURL=BitrateManager.js.map